import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Table from '@bexs/dollar/components/Table';
import { Loader } from '../../../../components/Loader';
import Card from '@bexs/dollar/components/Card';
import ErrorHandler from 'client/ErrorHandler';
import FilterBox from 'pages/Balance/sections/FilterBox';
import { useSelector } from 'store';
import { FormattedStatement } from 'store/ducks/statements/types';
import {
  tableColumns,
  ITEMS_PER_PAGE,
} from 'pages/Balance/sections/TableResults/config';
import statementListActions, {
  resetList,
} from 'store/ducks/statements/actions';
import OperationDetails from './sections/OperationDetails';
import { TableContainer } from './styles';
import TableActions from './sections/TableActions';

const TableResults: React.FC = () => {
  const dispatch = useDispatch();
  const { list, isLoading, error, hasMore } = useSelector().statements;
  const { periodEnd, periodStart, document, reportType } = useSelector().filter;

  const [isFilterBoxOpen, setIsFilterBoxOpen] = useState(false);

  const offset = useRef(0);

  const errorMessage = useMemo(() => ErrorHandler(error), [error]);

  const openFilterBox = () => setIsFilterBoxOpen(true);

  const closeFilterBox = () => setIsFilterBoxOpen(false);

  const makeStatementListActionsRequest = (reset = true) => {
    dispatch(
      statementListActions.request({
        periodStart,
        periodEnd,
        reportType,
        document,
        limit: ITEMS_PER_PAGE,
        offset: offset.current,
        update: !reset,
      })
    );
  };

  const loadData = async (reset = false) => {
    offset.current = reset ? 0 : offset.current + ITEMS_PER_PAGE;
    makeStatementListActionsRequest(reset);
  };

  useEffect(() => {
    dispatch(resetList());
    loadData(true);
  }, [dispatch, periodStart, periodEnd, reportType, document]);

  return (
    <Card
      title="Bank Statement"
      headerRightContent={<TableActions onFilterClick={openFilterBox} />}
      style={{ marginTop: '16px' }}
    >
      <FilterBox close={closeFilterBox} open={isFilterBoxOpen} />
      <TableContainer>
        {isLoading ? (
          <Loader isAbsolute isTransparent />
        ) : (
          <Table
            data={list}
            columns={tableColumns}
            forceEmptyRowMessageRender={list.length === 1}
            loadMore={{
              update: loadData,
              hasMore,
              scrollOnFinish: false,
            }}
            locale={{
              emptyMessage:
                errorMessage || 'No transactions found with applied filters.',
            }}
            expandable={{
              exclude: (row) => row.Description === 'WITHDRAW(REVERTED)',
              render: ({
                DocumentNumber: document,
                PayerBankName: originBankName,
                PayerAgencyNumber: originAgency,
                PayerAccountNumber: originAccount,
                ReceiverBankName: receiverBankName,
                ReceiverAccountNumber: receiverAccount,
                ReceiverAgencyNumber: receiverAgency,
                FullName: fullName,
                ReceiverDocumentIdFormatted: accountId,
                Type: operationType,
                IsIncomingOperation: isIncomingOperation,
              }: FormattedStatement) => (
                <OperationDetails
                  document={document}
                  fullName={fullName}
                  originBank={originBankName}
                  originAgency={originAgency}
                  originAccount={originAccount}
                  receiverBank={receiverBankName}
                  receiverAgency={receiverAgency}
                  receiverAccount={receiverAccount}
                  accountId={accountId}
                  operationType={operationType}
                  isIncomingOperation={isIncomingOperation}
                />
              ),
            }}
          />
        )}
      </TableContainer>
    </Card>
  );
};

export default TableResults;
